import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import {Button, Dialog, DialogActions, DialogContent, Grid, ListSubheader, Typography, IconButton, Tooltip,} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RelatedDocumentsTable from './RelatedDocumentsTable';
import RelatedDocumentsGet from './RelatedDocumentsGet';

const styles = (theme) => ({
    root: {
        height: '75vh',
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'wrap',
        justifyContent: 'space-around',
        overflow: 'hidden',
    },
    left: {
        float: 'left',
        width: '50%',
    },
    right: {
        float: 'right',
        width: '50%',
    },
    smallAvatar: {
        margin: 10,
        width: 30,
        height: 30,
    },
});

function RelatedDocumentsDialog(props) {
    const {
        classes,
        userDetails,
        metadataConfig,
        actionsConfig,
        folderDetails,
        triggerRefreshAuthToken,
        searchConfig,
        documentDetails,
    } = props;

    // Individual states
    const [open, setOpen] = useState(true)
    const [searchResults, setSearchResults] = useState([]);
    const [mountRelatedDocumentsTable, setMountRelatedDocumentsTable] = useState(false);
    const [mountRelatedDocumentsGet, setMountRelatedDocumentsGet] = useState(true);
    const [searchLimit, setSearchLimit] = useState(
        parseInt(process.env.REACT_APP_CONTENT_API_SEARCH_METADATA_LIMIT)
    );
    const [searchOffset, setSearchOffset] = useState(0);
    const [searchTotalCount, setSearchTotalCount] = useState(0);
    const [newSearch, setNewSearch] = useState(true);
    const [isFetching, setIsFetching] = useState(true);
    const [nextMarker, setNextMarker] = useState(undefined);

    // Mount dialog on first render
    useEffect(() => {
        setOpen(true);
    }, []);

    // === Handlers ===
    const handleCloseDialog = useCallback(() => {
        setOpen(false);
        props.handleCloseDialog();
    }, [handleCloseDialog]);

    const unmountRelatedDocumentsTable = useCallback(() => {
        setMountRelatedDocumentsTable(false);
    }, []);

    const remountRelatedDocumentsTable = useCallback(() => {
        setMountRelatedDocumentsTable(true);
    }, []);

    const unmountRelatedDocumentsGet = useCallback(() => {
        setMountRelatedDocumentsGet(false);
    }, []);

    const remountRelatedDocumentsGet = useCallback(() => {
        setMountRelatedDocumentsGet(true);
    }, []);

    const updateSearchResults = useCallback(
        (results, totalCount, isNewSearch, marker) => {
            window.location.pathname.toLowerCase().includes('debug') &&
            console.log('updateSearchResults', { results, totalCount, isNewSearch, marker });

            const newResults =
                searchOffset === 0 ? results : searchResults.concat(results);

            setSearchResults(newResults);
            setNextMarker(marker);
            setIsFetching(false);
            setMountRelatedDocumentsTable(isNewSearch);
            setSearchTotalCount(totalCount || 0);
            setSearchLimit(parseInt(process.env.REACT_APP_CONTENT_API_SEARCH_METADATA_LIMIT));
            setNewSearch(false);
        },
        [searchOffset, searchResults]
    );

    const getNextResults = useCallback(() => {
        setMountRelatedDocumentsGet(false);
        setNewSearch(false);
        setIsFetching(true);
        setSearchOffset((prev) => prev + searchLimit);
    }, [searchLimit]);

    // Debug log
    window.location.pathname.toLowerCase().includes('debug') &&
    console.log('RelatedDocumentsDialog props = ', props);

    return (
        <>
            {mountRelatedDocumentsGet && (
                <RelatedDocumentsGet
                    parentClasses={classes}
                    userDetails={userDetails}
                    metadataConfig={metadataConfig}
                    actionsConfig={actionsConfig}
                    folderDetails={folderDetails}
                    unmountComponent={unmountRelatedDocumentsGet}
                    remountComponent={remountRelatedDocumentsGet}
                    searchLimit={searchLimit}
                    searchOffset={searchOffset}
                    newSearch={newSearch}
                    updateSearchResults={updateSearchResults}
                    triggerRefreshAuthToken={triggerRefreshAuthToken}
                    nextMarker={nextMarker}
                    searchConfig={searchConfig}
                    actionConfig={actionsConfig.relatedDocuments}
                    documentDetails={documentDetails}
                />
            )}

            <Dialog
                open={open}
                onClose={props.handleCloseDilog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth
                maxWidth="xl"
            >
                <Tooltip title="Close">
                    <IconButton
                        aria-label="close"
                        onClick={(event, reason) => handleCloseDialog(event, reason)}
                        color="secondary"
                        sx={{ position: 'absolute', right: 0, top: 0 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Tooltip>

                <DialogContent
                    sx={(theme) => ({
                        paddingLeft: theme.spacing(4),
                        paddingRight: theme.spacing(4),
                        paddingTop: theme.spacing(4),
                    })}
                >
                    <Grid container spacing={1} style={{ height: '90vh' }}>
                        <Grid item xs={12} style={{ height: '100%', overflowY: 'auto' }}>
                            {isFetching && newSearch && (
                                <div>
                                    <Typography variant="subtitle1">
                                        <ListSubheader disableSticky>
                                            Retrieving related documents...
                                        </ListSubheader>
                                    </Typography>
                                </div>
                            )}

                            {mountRelatedDocumentsTable && (
                                <RelatedDocumentsTable
                                    searchConfig={searchConfig}
                                    searchResults={searchResults}
                                    userDetails={userDetails}
                                    nextMarker={nextMarker}
                                    searchTotalCount={searchTotalCount}
                                    getNextResults={getNextResults}
                                    isFetching={isFetching}
                                    searchLimit={searchLimit}
                                    unmountComponent={unmountRelatedDocumentsTable}
                                    remountComponent={remountRelatedDocumentsTable}
                                    actionsConfig={actionsConfig}
                                    triggerRefreshAuthToken={triggerRefreshAuthToken}
                                />
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button onClick={props.handleCloseDialog} variant="contained" color="secondary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

RelatedDocumentsDialog.propTypes = {
    userDetails: PropTypes.object.isRequired,
    documentDetails: PropTypes.object.isRequired,
    folderDetails: PropTypes.object.isRequired,
    actionsConfig: PropTypes.object.isRequired,
    searchConfig: PropTypes.object.isRequired,
    metadataConfig: PropTypes.object.isRequired,
    handleCloseDialog: PropTypes.func.isRequired,
    triggerRefreshAuthToken: PropTypes.func.isRequired,
};

export default withStyles(styles, { withTheme: true })(RelatedDocumentsDialog);
